import React from "react";
import { QualificationResult, Plan } from "../types";
import daveThumbsUp from "../assets/images/dave_thumb_up.svg";

interface PlansStepProps {
  qualificationResult: QualificationResult | null;
  onSelectPlan: (plan: Plan | null) => void;
  onBack: () => void;
  loading: boolean;
}

/**
 * Split a speed string like "2000 Mbps" into its number and unit so we can
 * style them independently (big bold number, small muted unit).
 */
const parseSpeed = (speed: string): { value: string; unit: string } => {
  if (!speed) return { value: speed, unit: "" };
  const m = speed.match(/^(\d+(?:\.\d+)?)\s*(\S+)$/);
  if (!m) return { value: speed, unit: "" };
  return { value: m[1], unit: m[2] };
};

const PlanCard: React.FC<{
  plan: Plan;
  loading: boolean;
  onSelect: (plan: Plan) => void;
}> = ({ plan, loading, onSelect }) => (
  <div className="group relative border-2 border-crisp-blue rounded-xl p-6 hover:shadow-2xl transition-all duration-300 bg-white hover:scale-105 cursor-pointer">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

    <div className="relative z-10">
      <div className="mb-3">
        <h3 className="text-lg font-bold text-crisp-dark-blue">{plan.name}</h3>
      </div>

      <div className="border-t-2 border-crisp-orange pt-4 mb-4">
        <div className="bg-orange-50 rounded-lg p-4 mb-3">
          <div className="flex justify-center items-baseline">
            <span className="text-3xl font-bold text-crisp-orange">
              {plan.price_formatted || "Contact us"}
            </span>
            <span className="text-sm text-crisp-grey ml-1">/month</span>
          </div>
        </div>
        {plan.setup_fee > 0 && (
          <div className="text-sm text-crisp-grey text-center">
            Setup: {plan.setup_fee_formatted}
          </div>
        )}
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-white rounded-lg border border-crisp-blue/20">
          <span className="text-sm text-crisp-grey">Download</span>
          <span className="whitespace-nowrap">
            <span className="font-bold text-crisp-blue text-base">
              {parseSpeed(plan.download_speed).value}
            </span>
            <span className="text-xs text-crisp-grey ml-1">
              {parseSpeed(plan.download_speed).unit}
            </span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-white rounded-lg border border-crisp-nbn/20">
          <span className="text-sm text-crisp-grey">Upload</span>
          <span className="whitespace-nowrap">
            <span className="font-bold text-crisp-nbn text-base">
              {parseSpeed(plan.upload_speed).value}
            </span>
            <span className="text-xs text-crisp-grey ml-1">
              {parseSpeed(plan.upload_speed).unit}
            </span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-white rounded-lg border border-crisp-orange/20">
          <span className="text-sm text-crisp-grey">Data</span>
          <span className="font-bold text-crisp-orange whitespace-nowrap text-base">
            {plan.data_allowance}
          </span>
        </div>
      </div>

      <button
        onClick={() => onSelect(plan)}
        disabled={loading}
        className="w-full bg-crisp-orange hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50"
      >
        Select Plan →
      </button>
    </div>
  </div>
);

const PlanGroup: React.FC<{
  heading: string;
  subheading?: string;
  accentClass: string;
  plans: Plan[];
  loading: boolean;
  onSelect: (plan: Plan) => void;
}> = ({ heading, subheading, accentClass, plans, loading, onSelect }) => (
  <section className="mb-8">
    <div className={`flex items-baseline gap-3 border-b-2 ${accentClass} pb-2 mb-4`}>
      <h3 className="text-xl font-bold text-crisp-dark-blue">{heading}</h3>
      {subheading && (
        <span className="text-sm text-crisp-grey">{subheading}</span>
      )}
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
      {plans.map((plan) => (
        <PlanCard
          key={plan.plan_id}
          plan={plan}
          loading={loading}
          onSelect={onSelect}
        />
      ))}
    </div>
  </section>
);

const PlansStep: React.FC<PlansStepProps> = ({
  qualificationResult,
  onSelectPlan,
  onBack,
  loading,
}) => {
  if (!qualificationResult) {
    return null;
  }

  if (!qualificationResult.serviceable) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-crisp-orange">
        <h2 className="text-2xl font-bold text-crisp-orange mb-4">
          Service Availability
        </h2>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
          <p className="text-gray-800 mb-4">{qualificationResult.message}</p>
          {qualificationResult.contact_message && (
            <p className="text-gray-600">
              {qualificationResult.contact_message}
            </p>
          )}
        </div>

        <div className="flex gap-4">
          <button
            onClick={onBack}
            className="flex-1 bg-gray-200 text-gray-700 font-bold py-3 px-6 rounded-lg hover:bg-gray-300 transition"
          >
            ← Back
          </button>
          {qualificationResult.allow_lead_creation && (
            <button
              onClick={() => onSelectPlan(null)}
              className="flex-1 bg-crisp-orange text-white font-bold py-3 px-6 rounded-lg hover:bg-opacity-90 transition"
            >
              Continue Anyway
            </button>
          )}
        </div>
      </div>
    );
  }

  const plans = qualificationResult.plans || [];
  const crispPlans = plans.filter((p) => p.provider === "crisp_wireless");
  const nbnPlans = plans.filter((p) => p.provider === "nbn");
  // Fallback: any plan without a provider tag (shouldn't happen, but safe)
  const untaggedPlans = plans.filter((p) => !p.provider);

  const upgradePlans = qualificationResult.upgrade_plans || [];
  const fttpUpgradeAvailable =
    !!qualificationResult.fttp_upgrade_available && upgradePlans.length > 0;

  const scrollToUpgrade = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById("fttp-upgrade-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Build the top-line summary without ever prefixing "NBN" to "CRISP"
  const parts: string[] = [];
  if (crispPlans.length > 0) parts.push("CRISP Wireless");
  if (nbnPlans.length > 0) {
    const nbnTech =
      qualificationResult.technology_description ||
      qualificationResult.technology ||
      "";
    // Strip any "CRISP Wireless + NBN " prefix that the backend may include
    const cleanTech = nbnTech.replace(/^CRISP Wireless\s*\+\s*NBN\s*/i, "");
    parts.push(`NBN ${cleanTech}`.trim());
  }
  const summary = parts.join(" and ");

  // Derive the NBN subheading (tech description) for the group header
  const nbnTechDescription = (() => {
    const desc =
      qualificationResult.technology_description ||
      qualificationResult.technology ||
      "";
    return desc.replace(/^CRISP Wireless\s*\+\s*NBN\s*/i, "").trim();
  })();

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-crisp-orange">
      {/* Header with Dave */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-crisp-orange mb-2">
            Available Plans
          </h2>
          {summary && (
            <p className="text-gray-700">
              Great news! We can provide service via{" "}
              <span className="font-bold text-crisp-orange">{summary}</span>
            </p>
          )}
          {fttpUpgradeAvailable && (
            <div className="mt-3 bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-crisp-blue rounded-r-lg p-3">
              <p className="text-sm text-crisp-dark-blue">
                <span className="font-bold">🎉 FTTP Upgrade Available!</span>{" "}
                You're eligible for a <strong>free upgrade to Fibre to the Premises</strong> from NBN.{" "}
                <a
                  href="#fttp-upgrade-section"
                  onClick={scrollToUpgrade}
                  className="text-crisp-blue font-bold underline hover:text-crisp-orange"
                >
                  Click here for more info
                </a>
              </p>
            </div>
          )}
        </div>
        <img
          src={daveThumbsUp}
          alt="Dave thumbs up"
          className="w-40 h-40 ml-4 flex-shrink-0"
        />
      </div>

      {crispPlans.length > 0 && (
        <PlanGroup
          heading="CRISP Wireless"
          subheading="Fixed Wireless from our local towers"
          accentClass="border-crisp-orange"
          plans={crispPlans}
          loading={loading}
          onSelect={onSelectPlan}
        />
      )}

      {nbnPlans.length > 0 && (
        <PlanGroup
          heading={`NBN${nbnTechDescription ? ` ${nbnTechDescription}` : ""}`}
          subheading="Fixed-line NBN services"
          accentClass="border-crisp-nbn"
          plans={nbnPlans}
          loading={loading}
          onSelect={onSelectPlan}
        />
      )}

      {untaggedPlans.length > 0 && (
        <PlanGroup
          heading="Available Plans"
          accentClass="border-crisp-blue"
          plans={untaggedPlans}
          loading={loading}
          onSelect={onSelectPlan}
        />
      )}

      {fttpUpgradeAvailable && (
        <section
          id="fttp-upgrade-section"
          className="mt-4 mb-8 border-2 border-crisp-blue rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 p-6 scroll-mt-6"
        >
          <div className="mb-4">
            <h3 className="text-2xl font-bold text-crisp-dark-blue mb-2">
              Fibre to the Premise Upgrade
            </h3>
            <p className="text-gray-700">
              <span className="font-bold text-crisp-blue">Good news:</span> NBN is offering a{" "}
              <span className="font-bold">free upgrade</span> from your current connection to{" "}
              <span className="font-bold">Fibre to the Premises (FTTP)</span> at this address.
              FTTP delivers full-fibre speeds straight to your home — much faster, more reliable,
              and future-proof.
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-1 text-sm text-gray-700">
              <li>
                <span className="font-semibold">Free installation</span> from NBN — no upgrade
                fees.
              </li>
              <li>
                <span className="font-semibold">12 month minimum commitment</span> on your chosen
                FTTP plan.
              </li>
              <li>
                Access to higher-tier speeds (up to 2000/200 Mbps) only available on FTTP.
              </li>
              <li>
                CRISP Wireless arranges the upgrade with NBN — you don't need to do anything else.
              </li>
            </ul>
            <p className="text-sm text-gray-600 mt-3 italic">
              Select an upgrade plan below if you'd like to proceed. We'll note this on your order
              and the CRISP team will arrange the FTTP upgrade with NBN before activating your
              service.
            </p>
          </div>

          <div className="border-b-2 border-crisp-blue pb-2 mb-4 mt-6">
            <h4 className="text-lg font-bold text-crisp-dark-blue">
              Plans available with FTTP upgrade
            </h4>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upgradePlans.map((plan) => (
              <PlanCard
                key={`upgrade-${plan.plan_id}`}
                plan={plan}
                loading={loading}
                onSelect={onSelectPlan}
              />
            ))}
          </div>
        </section>
      )}

      <button
        onClick={onBack}
        disabled={loading}
        className="text-crisp-orange hover:underline disabled:opacity-50"
      >
        ← Back
      </button>
    </div>
  );
};

export default PlansStep;
